import { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Typewriter from "typewriter-effect";
import moment from "moment";
import AiCorner from "../assets/ai_avatar.jpg";
import { marked } from "marked";

// 導入需要的 hooks 和 context
import { useDemand } from "../../../context/DemandContext";
import { useFilesByIds } from "../../../context/FileManagementContext";

// 配置 marked 選項
marked.setOptions({
  breaks: true, // 啟用換行符轉換為 <br>
  gfm: true, // 啟用 GitHub 風格的 Markdown
});

// 預處理 Markdown 文本，確保列表正確顯示
const preprocessMarkdown = (text) => {
  // 首先處理列表項之間的換行
  let processedText = text.replace(/^(\d+\..+)$/gm, "$1\n\n");

  // 處理有序列表 (1. 2. 等)
  processedText = processedText.replace(
    /^(\d+\.)(\s+)/gm,
    '<span class="inline-block min-w-[1.2em] font-bold">$1</span>$2',
  );

  // 處理無序列表 (*, -, +)
  processedText = processedText.replace(
    /^([*\-+])(\s+)/gm,
    '<span class="inline-block min-w-[1.2em] font-bold">$1</span>$2',
  );

  // 處理嵌套列表 (縮進的列表項)
  processedText = processedText.replace(
    /^(\s+)([*\-+]|\d+\.)(\s+)/gm,
    '$1<span class="inline-block min-w-[1.2em] font-bold">$2</span>$3',
  );

  return processedText;
};

const Message = ({ message }) => {
  const isAi = message.sender === "ai";
  const typewriterRef = useRef(null);
  const prevTextRef = useRef("");
  const contentRef = useRef(null);
  const initialRenderRef = useRef(true);

  // 從 DemandContext 獲取數據
  const { state } = useDemand();
  
  // 獲取頭像檔案 IDs
  const avatarFileIds = state.ceremony?.liveStream?.files?.my?.photo?.map(file => file.fileId) || [];
  const avatarFiles = useFilesByIds(avatarFileIds);
  
  // 獲取 AI 名稱
  const aiName = state.basic?.name || "Panco AI小助理";
  
  // 使用第一個頭像，如果沒有則使用預設圖片
  const avatarUrl = avatarFiles.length > 0 && avatarFiles[0]?.url ? avatarFiles[0].url : AiCorner;

  // 將 Markdown 轉換為 HTML (僅用於 AI 訊息)
  const convertMarkdownToHtml = (text) => {
    if (!isAi) return text; // 使用者訊息不轉換 Markdown
    const processedText = preprocessMarkdown(text);
    return marked.parse(processedText);
  };

  useEffect(() => {
    // 初次渲染時，讓 typewriter 完整顯示訊息
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    if (!typewriterRef.current || message.text === prevTextRef.current) return;

    // 計算新增的文字部分
    const newText = message.text.slice(prevTextRef.current.length);

    if (newText.length > 0) {
      // 如果有新文字，直接添加這部分文字
      const newContent = isAi ? convertMarkdownToHtml(newText) : newText;

      // 不刪除之前的內容，只添加新的部分
      typewriterRef.current.changeDelay(30).typeString(newContent).start();
    } else if (message.text.length < prevTextRef.current.length) {
      // 如果新訊息比舊訊息短，表示內容被刪減，需要重新打出所有內容
      const content = isAi ? convertMarkdownToHtml(message.text) : message.text;

      typewriterRef.current
        .deleteAll(1)
        .pauseFor(30)
        .changeDelay(30)
        .typeString(content)
        .start();
    }

    prevTextRef.current = message.text;
  }, [message.text, isAi]);

  // 如果是初始渲染，直接設置內容
  useEffect(() => {
    if (!contentRef.current) return;

    if (initialRenderRef.current && message.text) {
      const content = isAi ? convertMarkdownToHtml(message.text) : message.text;
      contentRef.current.innerHTML = content;
    }
  }, []);
  return (
    <div
      className={`flex w-fit max-w-[100%] flex-col gap-1 ${
        !isAi ? "self-end" : "self-start"
      }`}
    >
      {isAi && (
        <div className="flex items-center gap-3">
          <div className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white">
            <img
              src={avatarUrl}
              alt="ai-avatar"
              className="h-auto w-full object-cover"
            />
          </div>
          <span className="text-xs font-semibold text-white">{aiName}</span>
        </div>
      )}
      <div className={`flex gap-2 ${isAi ? "flex-row-reverse" : "flex-row"}`}>
        <div className="self-end text-xs text-[#000000]">
          {moment(message.createdAt).format("HH:mm")}
        </div>
        <div
          className={`w-full rounded p-2 leading-[1.2] ${
            message.sender === "ai"
              ? "ml-10 bg-[#00000080] text-white"
              : "bg-[#00000080]"
          }`}
          style={{
            wordBreak: "break-all",
          }}
        >
          <div className="markdown-content whitespace-pre-wrap font-medium text-white [&_li]:mb-1 [&_ol]:my-2 [&_ol]:pl-6 [&_ul]:my-2 [&_ul]:pl-6">
            <Typewriter
              autoStart={true}
              options={{
                cursor: "",
                delay: 30, // 調整打字速度
                html: isAi, // 只有 AI 訊息使用 HTML
                skipAddStyles: true,
                cursorClassName: "",
                wrapperClassName: "h-fit flex flex-col",
              }}
              onInit={(typewriter) => {
                typewriterRef.current = typewriter;
                const content = isAi
                  ? convertMarkdownToHtml(message.text)
                  : message.text;

                // 初始顯示訊息
                typewriter.changeDelay(30).typeString(content).start();

                prevTextRef.current = message.text;
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Message;

Message.propTypes = {
  message: PropTypes.shape({
    text: PropTypes.string.isRequired,
    sender: PropTypes.string.isRequired,
    createdAt: PropTypes.string,
    isLoading: PropTypes.bool,
  }).isRequired,
};
